// src/components/black-friday/product-comparison-view.tsx
import React, { useState, useMemo } from "react";
import { BFProductData, BFProductComparison } from "@/types/black-friday";
import {
  createProductComparison,
  filterProductComparisons,
  sortProductComparisons,
  determineMatchingMethod,
} from "@/lib/black-friday-product-comparison";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowUpIcon, ArrowDownIcon, ArrowUpDown, Info, Search } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import type { ComparisonPeriod } from "./dashboard-view";

interface Props {
  products25: BFProductData[];
  products24: BFProductData[];
  products23: BFProductData[];
  comparisonPeriod: ComparisonPeriod;
}

type ChangeDisplayMode = "absolute" | "percentage" | "both";
type FilterMode = "all" | "new" | "existing" | "missing";

export default function ProductComparisonView({
  products25,
  products24,
  products23,
  comparisonPeriod,
}: Props) {
  // Derive comparison year from period
  const comparisonYear = comparisonPeriod.startsWith('year2024') ? '2024' : '2023';

  const [changeMode, setChangeMode] = useState<ChangeDisplayMode>("percentage");
  const [filterMode, setFilterMode] = useState<FilterMode>("all");
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [limit, setLimit] = useState<number>(50);
  const [sortColumn, setSortColumn] = useState<string>("cost25");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  // Determine matching method based on data
  const matchingMethod = useMemo(() => {
    return determineMatchingMethod(products25);
  }, [products25]);

  // Create comparison data
  const comparisons = useMemo(() => {
    const compProducts = comparisonYear === "2024" ? products24 : products23;
    const compared = createProductComparison(products25, compProducts, matchingMethod);
    let filtered = filterProductComparisons(compared, filterMode);

    // Apply search filter
    if (searchFilter.trim()) {
      const searchLower = searchFilter.toLowerCase().trim();
      filtered = filtered.filter(product =>
        product.productTitle.toLowerCase().includes(searchLower) ||
        product.productId.toLowerCase().includes(searchLower)
      );
    }

    const sorted = sortProductComparisons(filtered, sortColumn, sortDirection);
    return sorted;
  }, [
    products25,
    products24,
    products23,
    comparisonPeriod,
    matchingMethod,
    filterMode,
    searchFilter,
    sortColumn,
    sortDirection,
  ]);

  const displayProducts = comparisons.slice(0, limit);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  // Helper to format change values
  const formatChange = (
    absolute: number,
    percentage: number,
    isPositiveGood: boolean = true
  ) => {
    const absFormatted =
      absolute >= 0
        ? `+${Math.round(absolute).toLocaleString()}`
        : Math.round(absolute).toLocaleString();
    const pctFormatted =
      percentage >= 0 ? `+${percentage.toFixed(1)}%` : `${percentage.toFixed(1)}%`;

    const isPositive = absolute >= 0;
    const color =
      (isPositiveGood && isPositive) || (!isPositiveGood && !isPositive)
        ? "text-green-600 dark:text-green-400"
        : "text-red-600 dark:text-red-400";

    switch (changeMode) {
      case "absolute":
        return <span className={color}>{absFormatted}</span>;
      case "percentage":
        return <span className={color}>{pctFormatted}</span>;
      case "both":
        return (
          <div className={color}>
            <div className="font-medium">{pctFormatted}</div>
            <div className="text-xs opacity-70">({absFormatted})</div>
          </div>
        );
    }
  };

  // Helper to truncate product title
  const truncateTitle = (title: string, maxLength: number = 70) => {
    if (title.length <= maxLength) return title;
    return title.substring(0, maxLength) + "...";
  };

  const SortableHeader = ({
    column,
    children,
    className,
  }: {
    column: string;
    children: React.ReactNode;
    className?: string;
  }) => (
    <TableHead
      className={`cursor-pointer hover:bg-muted/50 ${className || ""}`}
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center justify-end gap-1">
        {children}
        {sortColumn === column ? (
          sortDirection === "asc" ? (
            <ArrowUpIcon className="w-3 h-3" />
          ) : (
            <ArrowDownIcon className="w-3 h-3" />
          )
        ) : (
          <ArrowUpDown className="w-3 h-3 opacity-30" />
        )}
      </div>
    </TableHead>
  );

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-wrap gap-4">
        {/* Change Display Mode */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">Show change:</label>
          <Select
            value={changeMode}
            onValueChange={(value) => setChangeMode(value as ChangeDisplayMode)}
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="absolute">Absolute</SelectItem>
              <SelectItem value="percentage">Percentage</SelectItem>
              <SelectItem value="both">Both</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Filter Mode */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">Filter:</label>
          <Select
            value={filterMode}
            onValueChange={(value) => setFilterMode(value as FilterMode)}
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Products</SelectItem>
              <SelectItem value="new">New (2025 only)</SelectItem>
              <SelectItem value="existing">Existing (both years)</SelectItem>
              <SelectItem value="missing">Missing (no longer seen)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Search Filter */}
        <div className="flex items-center gap-2 flex-1 min-w-[250px]">
          <label className="text-sm font-medium">Search:</label>
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Filter products..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Limit */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">Show:</label>
          <Select
            value={limit.toString()}
            onValueChange={(value) => setLimit(parseInt(value, 10))}
          >
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="50">Top 50</SelectItem>
              <SelectItem value="100">Top 100</SelectItem>
              <SelectItem value="200">Top 200</SelectItem>
              <SelectItem value="500">Top 500</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            {/* Main header row with column groups */}
            <TableRow>
              <TableHead
                className="text-left border-r bg-background font-semibold sticky left-0 z-10"
              >
                Product
              </TableHead>
              <TableHead
                colSpan={4}
                className="text-center border-r bg-blue-50 dark:bg-blue-950/20 font-semibold"
              >
                2025 Performance (so far)
              </TableHead>
              <TableHead
                colSpan={4}
                className="text-center border-r bg-gray-50 dark:bg-gray-900/20 font-semibold"
              >
                {comparisonYear} Performance (full 12-week period)
              </TableHead>
              <TableHead
                colSpan={4}
                className="text-center bg-amber-50 dark:bg-amber-950/20 font-semibold"
              >
                Change (2025 vs {comparisonYear})
              </TableHead>
            </TableRow>
            {/* Detailed column headers */}
            <TableRow>
              {/* Product Title column */}
              <TableHead
                className="sticky left-0 bg-background z-10 border-r cursor-pointer hover:bg-muted/50 min-w-[250px]"
                onClick={() => handleSort("productTitle")}
              >
                <div className="flex items-center gap-1">
                  Product Title
                  {sortColumn === "productTitle" ? (
                    sortDirection === "asc" ? (
                      <ArrowUpIcon className="w-3 h-3" />
                    ) : (
                      <ArrowDownIcon className="w-3 h-3" />
                    )
                  ) : (
                    <ArrowUpDown className="w-3 h-3 opacity-30" />
                  )}
                </div>
              </TableHead>

              {/* 2025 Metrics */}
              <SortableHeader
                column="impressions25"
                className="text-right bg-blue-50/50 dark:bg-blue-950/10"
              >
                Impr
              </SortableHeader>
              <SortableHeader
                column="cost25"
                className="text-right bg-blue-50/50 dark:bg-blue-950/10"
              >
                Cost
              </SortableHeader>
              <SortableHeader
                column="conv25"
                className="text-right bg-blue-50/50 dark:bg-blue-950/10"
              >
                Conv
              </SortableHeader>
              <SortableHeader
                column="convValue25"
                className="text-right border-r bg-blue-50/50 dark:bg-blue-950/10"
              >
                Value
              </SortableHeader>

              {/* Comparison Year Metrics */}
              <SortableHeader
                column="impressionsComp"
                className="text-right bg-gray-50/50 dark:bg-gray-900/10"
              >
                Impr
              </SortableHeader>
              <SortableHeader
                column="costComp"
                className="text-right bg-gray-50/50 dark:bg-gray-900/10"
              >
                Cost
              </SortableHeader>
              <SortableHeader
                column="convComp"
                className="text-right bg-gray-50/50 dark:bg-gray-900/10"
              >
                Conv
              </SortableHeader>
              <SortableHeader
                column="convValueComp"
                className="text-right border-r bg-gray-50/50 dark:bg-gray-900/10"
              >
                Value
              </SortableHeader>

              {/* Change Metrics */}
              <SortableHeader
                column={
                  changeMode === "absolute" ? "impressionsChange" : "impressionsChangePct"
                }
                className="text-right bg-amber-50/50 dark:bg-amber-950/10"
              >
                Impr Δ
              </SortableHeader>
              <SortableHeader
                column={changeMode === "absolute" ? "costChange" : "costChangePct"}
                className="text-right bg-amber-50/50 dark:bg-amber-950/10"
              >
                Cost Δ
              </SortableHeader>
              <SortableHeader
                column={changeMode === "absolute" ? "convChange" : "convChangePct"}
                className="text-right bg-amber-50/50 dark:bg-amber-950/10"
              >
                Conv Δ
              </SortableHeader>
              <SortableHeader
                column={changeMode === "absolute" ? "convValueChange" : "convValueChangePct"}
                className="text-right bg-amber-50/50 dark:bg-amber-950/10"
              >
                Value Δ
              </SortableHeader>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayProducts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={13} className="text-center py-12">
                  <div className="flex flex-col items-center gap-2">
                    <p className="text-muted-foreground font-medium">
                      No products match the current filter
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Try adjusting your filter settings
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              displayProducts.map((product, index) => (
                <TableRow key={index} className="hover:bg-muted/50">
                  {/* Product Title - wrap to 2 lines, truncated */}
                  <TableCell
                    className="sticky left-0 bg-background z-10 border-r min-w-[250px] max-w-[400px]"
                    title={product.productTitle}
                  >
                    <div className="line-clamp-2 text-sm">
                      {truncateTitle(product.productTitle)}
                      {!product.hasComparisonData && (
                        <span className="ml-2 text-xs font-semibold text-blue-600">[N]</span>
                      )}
                    </div>
                  </TableCell>

                  {/* 2025 Metrics */}
                  <TableCell className="text-right bg-blue-50/30 dark:bg-blue-950/10">
                    {product.impressions25.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right bg-blue-50/30 dark:bg-blue-950/10">
                    $
                    {product.cost25.toLocaleString(undefined, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </TableCell>
                  <TableCell className="text-right bg-blue-50/30 dark:bg-blue-950/10">
                    {product.conv25.toLocaleString(undefined, {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    })}
                  </TableCell>
                  <TableCell className="text-right border-r bg-blue-50/30 dark:bg-blue-950/10">
                    $
                    {product.convValue25.toLocaleString(undefined, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </TableCell>

                  {/* Comparison Year Metrics */}
                  <TableCell className="text-right bg-gray-50/30 dark:bg-gray-900/10">
                    {product.impressionsComp.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right bg-gray-50/30 dark:bg-gray-900/10">
                    $
                    {product.costComp.toLocaleString(undefined, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </TableCell>
                  <TableCell className="text-right bg-gray-50/30 dark:bg-gray-900/10">
                    {product.convComp.toLocaleString(undefined, {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    })}
                  </TableCell>
                  <TableCell className="text-right border-r bg-gray-50/30 dark:bg-gray-900/10">
                    $
                    {product.convValueComp.toLocaleString(undefined, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </TableCell>

                  {/* Change Metrics */}
                  <TableCell className="text-right bg-amber-50/30 dark:bg-amber-950/10">
                    {formatChange(
                      product.impressionsChange,
                      product.impressionsChangePct,
                      true
                    )}
                  </TableCell>
                  <TableCell className="text-right bg-amber-50/30 dark:bg-amber-950/10">
                    {formatChange(product.costChange, product.costChangePct, false)}
                  </TableCell>
                  <TableCell className="text-right bg-amber-50/30 dark:bg-amber-950/10">
                    {formatChange(product.convChange, product.convChangePct, true)}
                  </TableCell>
                  <TableCell className="text-right bg-amber-50/30 dark:bg-amber-950/10">
                    {formatChange(
                      product.convValueChange,
                      product.convValueChangePct,
                      true
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Table Footer with Stats */}
      <div className="text-sm text-muted-foreground text-center">
        Showing {displayProducts.length} of {comparisons.length} products
      </div>
    </div>
  );
}
